/**
 * Datas e horarios em pt-BR / America/Sao_Paulo.
 *
 * Duas regras que atravessam o sistema:
 *
 * 1. Instantes (publicacao de noticia, inicio de evento, vigencia de aviso)
 *    sao `timestamptz` - UTC no banco, convertidos apenas na borda.
 * 2. Horario de missa e HORA DE PAREDE (`dayOfWeek` + `HH:mm`), nunca um
 *    instante. "Domingo as 19h" continua sendo 19h depois de qualquer mudanca
 *    de fuso ou horario de verao; guardar como timestamp faria o horario
 *    anunciado mudar sozinho.
 */

export const TIMEZONE = 'America/Sao_Paulo'
export const LOCALE = 'pt-BR'

/** 0 = domingo, conforme Date.getDay() e a coluna MassSchedule.dayOfWeek. */
export const WEEKDAYS = [
  { value: 0, label: 'Domingo', short: 'Dom' },
  { value: 1, label: 'Segunda-feira', short: 'Seg' },
  { value: 2, label: 'Terca-feira', short: 'Ter' },
  { value: 3, label: 'Quarta-feira', short: 'Qua' },
  { value: 4, label: 'Quinta-feira', short: 'Qui' },
  { value: 5, label: 'Sexta-feira', short: 'Sex' },
  { value: 6, label: 'Sabado', short: 'Sab' },
] as const

export type Weekday = (typeof WEEKDAYS)[number]['value']

export function weekdayLabel(day: number): string {
  return WEEKDAYS.find((w) => w.value === day)?.label ?? '-'
}

export function weekdayShort(day: number): string {
  return WEEKDAYS.find((w) => w.value === day)?.short ?? '-'
}

function toDate(value: Date | string | number): Date {
  return value instanceof Date ? value : new Date(value)
}

export function formatDate(value: Date | string | number): string {
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(toDate(value))
}

export function formatDateLong(value: Date | string | number): string {
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: TIMEZONE,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(toDate(value))
}

export function formatDateTime(value: Date | string | number): string {
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(toDate(value))
}

export function formatTime(value: Date | string | number): string {
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
  }).format(toDate(value))
}

/** Hora de parede como a paroquia anuncia: 19:00 -> 19h, 19:30 -> 19h30. */
export function formatWallClock(time: string): string {
  const [hour, minute] = time.split(':')
  if (!hour) return time
  return minute && minute !== '00' ? Number(hour) + 'h' + minute : Number(hour) + 'h'
}

/**
 * Formata uma DATA CIVIL - coluna `@db.Date`, que o Prisma entrega como
 * meia-noite UTC e que nao tem hora nenhuma associada.
 *
 * Formatada em UTC de proposito. Passar meia-noite UTC pelo fuso da paroquia
 * (UTC-3) recua um dia: uma excecao lancada para 04/10 seria anunciada como
 * 03/10, e o fiel chegaria na igreja no dia errado.
 */
export function formatCivilDate(value: Date | string): string {
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(toDate(value))
}

export function formatCivilDateLong(value: Date | string): string {
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(toDate(value))
}

/** Data civil (YYYY-MM-DD) do instante, no fuso da paroquia. */
export function toISODateInTimezone(value: Date | string | number): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(toDate(value))
}

/** Dia da semana (0-6) do instante, no fuso da paroquia. */
export function weekdayInTimezone(value: Date | string | number): Weekday {
  const name = new Intl.DateTimeFormat('en-US', {
    timeZone: TIMEZONE,
    weekday: 'short',
  }).format(toDate(value))
  const map: Record<string, Weekday> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  }
  return map[name] ?? 0
}

/**
 * Janela de N dias a partir de hoje, como datas civis (YYYY-MM-DD) no fuso da
 * paroquia. Usada para casar a grade recorrente de missas com as excecoes.
 */
export function upcomingDates(days: number, from: Date = new Date()): string[] {
  const out: string[] = []
  for (let i = 0; i < days; i += 1) {
    out.push(toISODateInTimezone(new Date(from.getTime() + i * 86400000)))
  }
  return out
}

/** O instante esta dentro da janela? Usado na vigencia do aviso urgente. */
export function isWithinWindow(
  now: Date,
  startsAt: Date | string | null | undefined,
  endsAt: Date | string | null | undefined,
): boolean {
  if (startsAt && now < toDate(startsAt)) return false
  if (endsAt && now > toDate(endsAt)) return false
  return true
}

/** Valor para input[type=datetime-local], no fuso da paroquia. */
export function toDateTimeLocalValue(value: Date | string | number): string {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
    .format(toDate(value))
    .replace(' ', 'T')
}
